import Vue from "vue";
import Vuex from "vuex";
import VuexPersistence from "vuex-persist";
import qs from "qs";

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
});

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    projectName: null,
    orgName: null,

    token: null,

    duty: null,

    notifyBatchCount: null,
    logoImageName: "",

    grades: ["一年级", "二年级", "三年级", "四年级", "五年级"],
  },

  getters: {
    isProd: () => {
      const isProd = process.env.NODE_ENV === "production";
      return isProd;
    },

    isUserInRole: (state) => (rolename) => {
      if (!state.token?.roles || !Array.isArray(state.token?.roles))
        return false;
      return state.token.roles.includes(rolename);
    },

    formatClazzName: (state) => (clazz) => {
      if (!clazz) return "";
      return `${clazz.year}级${parseInt(clazz.name).toLocaleString(undefined, {
        minimumIntegerDigits: 2,
        useGrouping: false,
      })}班`;
    },

    formatFraction:
      (state) =>
      (num, maximumFractionDigits = 2, minimumFractionDigits = 2) => {
        if (isNaN(num)) return "-";
        if (num === null) return "-";
        return num.toLocaleString(undefined, {
          maximumFractionDigits,
          minimumFractionDigits,
          useGrouping: false,
        });
      },

    formatPercent:
      (state) =>
      (num, maximumFractionDigits = 2, minimumFractionDigits = 2) => {
        if (isNaN(num)) return "";
        if (!num && typeof num !== "number") return "";
        return num.toLocaleString(undefined, {
          style: "percent",
          maximumFractionDigits,
          minimumFractionDigits,
        });
      },

    formatTableColumnFraction:
      (state, getters) =>
      (maximumFractionDigits = 2, minimumFractionDigits = 2) =>
      (row, column, cellValue, index) => {
        return getters.formatFraction(
          cellValue,
          maximumFractionDigits,
          minimumFractionDigits
        );
      },

    formatTableColumnPercent:
      (state, getters) =>
      (maximumFractionDigits = 2, minimumFractionDigits = 2) =>
      (row, column, cellValue, index) => {
        return getters.formatPercent(
          cellValue,
          maximumFractionDigits,
          minimumFractionDigits
        );
      },
  },

  mutations: {
    afterLogin(state) {
      state.token = JSON.parse(window.localStorage.getItem("token"));
      let str = window.localStorage.getItem("sceneSetting");

      let sceneSetting;
      if (str) {
        sceneSetting = JSON.parse(str);
        state.sceneSetting = sceneSetting;
      }
    },

    chooseDuty(state, duty) {
      state.duty = duty;
      window.localStorage.setItem("duty", duty);
    },

    clearDuty(state) {
      state.duty = null;
      window.localStorage.removeItem("duty");
    },

    setProjectName(state, projectName) {
      state.projectName = projectName;
    },

    setOrgName(state, orgName) {
      state.orgName = orgName;
    },

    setLogoImageName(state, s) {
      state.logoImageName = s;
    },

    setNotifyBatchCount(state, count) {
      state.notifyBatchCount = count;
    },
  },

  actions: {
    getProjectName: async function ({ commit, state }) {
      return await new Promise((resolve, reject) => {
        commit("setProjectName", "学生体质健康监测平台");
        resolve(state.projectName);
      });
    },

    getOrgName: async function ({ commit, state }) {
      return await new Promise((resolve, reject) => {
        Vue.axios.get(`/api/configs/org-name`).then((res) => {
          const name = res.data;
          commit("setOrgName", name);
          resolve(state.orgName);
        });
      });
    },

    getLogoImageName: async function ({ commit, state }) {
      if (state.logoImageName && false) {
        return state.logoImageName;
      }
      return await new Promise((resolve, reject) => {
        Vue.axios.get(`/api/configs/school-icon`).then((res) => {
          const name = res.data;
          commit("setLogoImageName", name);
          resolve(state.logoImageName);
        });
      });
    },

    findEntity: async function ({}, url) {
      let res = await Vue.axios.get(url);
      return res.data;
    },

    findEntities: async function ({}, { url = "", params = {} }) {
      let res = await Vue.axios.get(url, {
        params,
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: "repeat" });
        },
      });
      return res.data;
    },

    findAllEntities: async function ({ dispatch }, { url = "", params = {} }) {
      let dataTable,
        page = 0,
        datas = [];
      do {
        dataTable = await dispatch("findEntities", {
          url,
          params: {
            ...params,
            page,
          },
        });
        page++;
        datas.push(...dataTable.content);
      } while (!dataTable.last);
      return datas;
    },

    findBatchById: async function ({}, batchId) {
      return await new Promise((resolve, reject) => {
        Vue.axios
          .get(`/api/batches/${batchId}`)
          .then((res) => {
            resolve(res.data);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    queryPreviewBatch: async function ({}, batchId) {
      try {
        let res = await Vue.axios.get(`/api/batches/${batchId}/preview`);
        return res.data;
      } catch (err) {
        return null;
      }
    },

    findSchoolBatchById: async function ({}, schoolBatchId) {
      return await new Promise((resolve, reject) => {
        Vue.axios
          .get(`/api/school-batches/${schoolBatchId}`)
          .then((res) => {
            resolve(res.data);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    findCoursesByGrade: async function ({}, grade) {
      if (!grade) {
        let res = await Vue.axios.get(`/api/courses`);
        return res.data;
      } else {
        return await new Promise((resolve, reject) => {
          Vue.axios
            .get(`/api/course-weights`, {
              params: {
                grade,
                size: 100,
              },
            })
            .then((res) => {
              let courseWeights = res.data.content;
              resolve(courseWeights.map((cw) => cw.course));
            })
            .catch((err) => reject(err));
        });
      }
    },

    findStudentsByClazzId: async function ({}, clazzId) {
      let res,
        page = 0,
        datas = [];
      do {
        res = await Vue.axios.get(`/api/students`, {
          params: {
            clazzId,
            page,
          },
        });
        page++;
        datas.push(...res.data.content);
      } while (!res.data.last);
      return datas;
    },

    findExamRecordsByBatchId: async function ({}, batchId) {
      let res,
        page = 0,
        datas = [];
      do {
        res = await Vue.axios.get(`/api/exam-records`, {
          params: {
            batchId,
            page,
          },
        });
        page++;
        datas.push(...res.data.content);
      } while (!res.data.last);
      return datas;
    },

    findVisionInfos: async function (
      {},
      { batchId, studentId, page = 1, size = 10 }
    ) {
      let res;
      res = await Vue.axios.get(`/api/vision-infos`, {
        params: {
          batchId,
          studentId,
          page: page - 1,
          size,
        },
      });
      return res.data;
    },

    findVisionInfo: async function ({}, { batchId, studentId }) {
      let res;
      res = await Vue.axios.get(`/api/vision-infos`, {
        params: {
          batchId,
          studentId,
        },
      });
      if (res.data.totalElements > 1) throw new Error("数据多于1条");
      return res.data.empty ? null : res.data.content[0];
    },

    findVisionInfosByBatchId: async function ({}, batchId) {
      let res,
        datas = [];
      res = await Vue.axios.get(`/api/vision-infos/batch/${batchId}`);
      datas.push(...res.data);
      return datas;
    },

    findCourseWeightLevelsByGrade: async function ({}, grade) {
      let res,
        page = 0,
        datas = [];
      do {
        res = await Vue.axios.get(`/api/course-weight-levels`, {
          params: {
            grade,
            page,
          },
        });
        page++;
        datas.push(...res.data.content);
      } while (!res.data.last);
      return datas;
    },

    findCourseExtraLevelsByGrade: async function ({}, grade) {
      let res,
        page = 0,
        datas = [];
      do {
        res = await Vue.axios.get(`/api/course-extra-levels`, {
          params: {
            grade,
            page,
          },
        });
        page++;
        datas.push(...res.data.content);
      } while (!res.data.last);
      return datas;
    },

    findClazzesByGradeYear: async function ({}, year) {
      let res,
        page = 0,
        datas = [];
      do {
        res = await Vue.axios.get(`/api/clazzes`, {
          params: {
            year,
            page,
          },
        });
        page++;
        datas.push(...res.data.content);
      } while (!res.data.last);
      return datas;
    },

    findBatchBySchoolBatchAndClazz: async function ({}, params) {
      let res = await Vue.axios.get(`/api/batches`, {
        params,
      });
      if (res.data.empty) return null;
      return res.data.content[0];
    },

    findFitnessBasicInfos: async function ({}, batchId) {
      let res,
        page = 0,
        datas = [];
      do {
        res = await Vue.axios.get(`/api/fitness-basic-infos`, {
          params: {
            batchId,
            page,
          },
        });
        page++;
        datas.push(...res.data.content);
      } while (!res.data.last);
      return datas;
    },

    queryGradeLevels: async function ({}) {
      let res = await Vue.axios.get(`/api/enums/grade-levels`);
      return res.data;
    },

    queryFitnessBasicInfoFields: async function ({}) {
      let res = await Vue.axios.get(`/api/enums/fitness-basic-info-fields`);
      return res.data;
    },

    querySexes: async function ({}) {
      let res = await Vue.axios.get(`/api/enums/sexes`);
      return res.data;
    },

    queryCountNotifyBatch: async function ({ commit }, clazzId) {
      let res = await Vue.axios.get(`/api/batches`, {
        params: {
          clazzId,
          notify: true,
          size: 1,
        },
      });
      const count = res.data.totalElements;
      commit("setNotifyBatchCount", count);
    },

    getCountNotifyBatch: async function ({ state, dispatch }, clazzId) {
      if (typeof state.notifyBatchCount === "number")
        return state.notifyBatchCount;
      await dispatch("queryCountNotifyBatch", clazzId);
      return state.notifyBatchCount;
    },
  },

  modules: {},

  plugins: [vuexLocal.plugin],
});
